import { useRouter } from 'vue-router';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import useStore from '~/composables/useStore';
import useDashboard from '~/composables/useDashboard';

/**
 * Où la déconnexion d'annuaire doit reprendre, le temps que Cognito rende la main.
 *
 * `sessionStorage` et non `localStorage` : la reprise ne concerne que cet onglet, et une
 * navigation interrompue ne doit pas laisser un autre onglet partir vers l'annuaire.
 */
const PENDING_DIRECTORY_LOGOUT = 'keyprod.sso.pendingDirectoryLogout';

/**
 * Ajoute à l'URL de déconnexion de quoi ramener la personne sur Keyprod.
 *
 * Sans ce paramètre, l'annuaire laisse le navigateur sur sa propre page de confirmation, et
 * la déconnexion se termine chez Microsoft plutôt que chez nous. Laissée telle quelle si
 * l'URL le porte déjà : le paramètre du tenant fait alors autorité.
 *
 * @param {string} directoryLogoutUrl
 * @returns {string}
 */
function withReturnToKeyprod(directoryLogoutUrl) {
  try {
    const url = new URL(directoryLogoutUrl);

    if (!url.searchParams.has('post_logout_redirect_uri')) {
      url.searchParams.set('post_logout_redirect_uri', `${window.location.origin}/login`);
    }

    return url.toString();
  } catch (error) {
    // URL du tenant illisible : on la suit telle quelle plutôt que de renoncer à sortir de
    // l'annuaire.
    return directoryLogoutUrl;
  }
}

export default function useAuth() {
  /**
   * Use store composable
   * @type {Store<any> | *}
   */
  const store = useStore();

  const router = useRouter();

  // Dashboard composable utilities
  const {
    sortDashboards,
    sortedUserDashboards,
  } = useDashboard();

  const computedUserDashboards = sortedUserDashboards;

  const activeUser = computed(() => store.getters['auth/user']);

  /**
   * Function logout
   * @type {methods}
   * @returns {Promise<boolean>} Faux quand Cognito a refusé la déconnexion, donc qu'aucune
   *                             navigation n'en est partie.
   */
  async function logout() {
    // Rendu, et non avalé : sur une session ouverte par l'annuaire, c'est `signOut()` qui
    // porte la navigation vers le `/logout` de Cognito. Son échec ne se voit nulle part
    // ailleurs, et l'appelant qui le croirait parti laisserait l'utilisateur sur place.
    let partiVersLAnnuaire = true;

    try {
      await signOut({ global: false });
    } catch (e) {
      console.error(e);
      // Refresh token might have been revoked server-side.
      // So the above signOut() call might fail.
      partiVersLAnnuaire = false;
    }

    // Force storage to be cleared.
    window.localStorage.clear();

    // Log out the user.
    await store.dispatch('auth/logout');

    return partiVersLAnnuaire;
  }

  /**
   * Oublie sur place une session d'annuaire que Keyprod refuse.
   *
   * `logout()` ferait l'affaire, mais sur une session ouverte par l'annuaire il PART :
   * `signOut()` navigue vers le `/logout` de Cognito, et la personne traverse des écrans
   * vides avant de revenir lire pourquoi on ne l'a pas fait entrer. Le refus est le nôtre, il
   * se prononce donc chez nous : la page de connexion s'affiche sur place, sans identité.
   *
   * Vider le stockage suffit à le tenir : c'est là qu'Amplify garde ses jetons, et sans eux
   * `getCurrentUser()` ne reconnaît plus personne. Le rechargement suivant repart donc d'une
   * page de connexion ordinaire, au lieu de rejouer le refus en boucle.
   *
   * La session hébergée de Cognito, elle, survit. C'est assumé : la refermer demanderait de
   * déconnecter aussi l'annuaire de l'entreprise, ce qu'un clic sur notre écran n'a pas à
   * faire. Une nouvelle tentative rencontrera le même refus, et le même message.
   */
  async function forgetDirectorySession() {
    window.localStorage.clear();

    await store.dispatch('auth/logout');
  }

  /**
   * Tells whether THIS session was opened through the directory.
   *
   * Le mode déclaré sur le compte ne le dit pas : un administrateur porte les deux voies, et
   * il entre par l'une ou par l'autre selon le jour. Se fier à lui enverrait vers le
   * sélecteur de comptes de l'annuaire quelqu'un qui vient de taper son mot de passe, et
   * laisserait ouverte la session d'annuaire de celui qui est entré par là.
   *
   * Seule la session sait par où elle est venue : Cognito pose `identities` sur le jeton
   * d'identité d'un compte fédéré, et rien du tout sur un compte natif.
   *
   * Une session illisible compte pour une entrée par mot de passe : on ferme alors ce qu'on
   * peut plutôt que d'envoyer quelqu'un vers un annuaire dont on ne sait rien.
   */
  async function signedInThroughDirectory() {
    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: false });
      const identities = tokens?.idToken?.payload?.identities;

      return Array.isArray(identities) && identities.length > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Ferme la session Cognito, puis celle de l'annuaire.
   *
   * Deux jambes, et seule la seconde distingue ce chemin de `logout()`. Elle est réservée à
   * la déconnexion **volontaire** : renvoyer vers le sélecteur de comptes de l'annuaire un
   * opérateur dont la session a simplement expiré lui ferait croire qu'il doit se
   * réauthentifier ailleurs. La modale d'expiration et les refus restent donc sur `logout()`.
   *
   * L'URL vient du paramètre du tenant, jamais devinée : une URL fabriquée qui rendrait 404
   * laisserait croire à une déconnexion propagée alors que la session d'annuaire resterait
   * ouverte. Son absence est un cas courant — seule la commande d'ouverture de surface
   * l'écrit — donc on ferme ce qu'on peut et on signale le reste.
   *
   * Limite connue, non corrigeable ici : l'annuaire intercale un choix de compte et n'éteint
   * la session qu'après ce clic. Qui ferme l'onglet au lieu de confirmer laisse sa session
   * d'annuaire ouverte. La sauter demanderait `logout_hint` ou `id_token_hint`, que Cognito
   * ne remet pas au front.
   */
  async function logoutFromDirectory() {
    const directoryLogoutUrl = window?.cognito?.sso_logout_url;

    if (!directoryLogoutUrl) {
      // eslint-disable-next-line no-console
      console.warn('Signed out of Keyprod only: this tenant carries no directory sign-out URL');

      await logout();

      return false;
    }

    // Retenue plutôt que suivie tout de suite : sur une session OAuth, `signOut()` part lui
    // aussi en navigation, vers le `/logout` de Cognito. Prendre la main juste après annulait
    // ce départ, et la session hébergée survivait à la déconnexion. On note donc où aller,
    // on laisse Amplify partir, et `resumeDirectoryLogout()` reprend au retour sur /login.
    try {
      window.sessionStorage.setItem(
        PENDING_DIRECTORY_LOGOUT,
        withReturnToKeyprod(directoryLogoutUrl),
      );
    } catch (error) {
      // Stockage indisponible (navigation privée, cookies bloqués) : on ferme au moins la
      // session Keyprod plutôt que de renoncer à la déconnexion.
      // eslint-disable-next-line no-console
      console.warn('Could not remember the directory sign-out step', error);
    }

    // Le départ ne s'annonce que s'il a lieu. Quand `signOut()` échoue — jeton de
    // rafraîchissement révoqué côté serveur —, aucune navigation ne part : l'appelant doit
    // reprendre la main et renvoyer vers /login, où `resumeDirectoryLogout()` rejouera
    // l'étape annuaire notée juste au-dessus.
    return logout();
  }

  /**
   * Poursuit la déconnexion d'annuaire, une fois Cognito revenu sur /login.
   *
   * Appelée au montage de la page de connexion : c'est là que le `/logout` de Cognito ramène
   * le navigateur, et c'est le seul moment où la seconde jambe peut partir sans écraser la
   * première.
   *
   * @returns {boolean} Vrai quand une navigation vers l'annuaire a été lancée.
   */
  function resumeDirectoryLogout() {
    let pending = null;

    try {
      pending = window.sessionStorage.getItem(PENDING_DIRECTORY_LOGOUT);
      window.sessionStorage.removeItem(PENDING_DIRECTORY_LOGOUT);
    } catch (error) {
      return false;
    }

    if (!pending) {
      return false;
    }

    window.location.assign(pending);

    return true;
  }

  /**
   * Redirect to login page
   */
  async function redirectToLogin(redirectAfterLogin = '') {
    const query = {};
    if (redirectAfterLogin) {
      query.redirect = redirectAfterLogin;
    }

    await router.push({ name: 'login', query }, () => {
      // Reset dashboard navigation period value
      store.commit('navigation/resetPeriodValue');
      // S-0025: drop the sticky relative period too — it must not leak into
      // the next session (review reco P3).
      store.commit('navigation/setActiveShortcut', null);
    });
  }

  /**
   * Tell if the actual user entity is me
   *
   * @param {object} userToTest
   * @return {boolean}
   */
  const userIsMe = (
    (userToTest) => userToTest && activeUser && activeUser.value.id === userToTest.id
  );

  /**
   * Tell if the actual user is an administrator/manager
   */
  const activeUserIsAdministratorOrManager = computed(() => ['administrator',
    'manager'].indexOf(activeUser.value?.access_code) >= 0);

  /**
   * Tell if the current user is an operator
   */
  const activeUserIsOperator = computed(() => activeUser.value?.access_code === 'operator');

  /**
   * Tell if it is the first login of the connected user
   */
  const userFirstLogin = computed(() => activeUser.value?.firstLogin === null);

  /**
   * user name
   */
  const userName = computed(() => (activeUser.value.first_name === '' ? activeUser.value.name : activeUser.value.first_name));

  // User access code
  const userAccessCode = computed(() => (activeUser.value ? activeUser.value.access_code : -1));

  // Tell if the actual user is a simplified operator
  const activeUserIsSimplifiedOperator = computed(() => userAccessCode.value === 'simplified_operator');

  const avatarUrl = computed(() => activeUser.value.imageUrl);

  const { t } = useI18n();

  /**
   * Pages that will be displayed on the list of starting pages
   */
  const allowedHomepages = computed(() => {
    const pages = [
      ...(!activeUserIsOperator.value
        ? [{
          dashboardId: '0',
          dashboardName: t('user.settings'),
        }]
        : []),
      {
        dashboardId: '1',
        dashboardName: t('navigation.drawer.dashboard.label.events'),
      },
      ...(!activeUserIsOperator.value
        ? [{
          dashboardId: '2',
          dashboardName: t('menu.primary.planning.planning'),
        }]
        : []),
      {
        dashboardId: '3',
        dashboardName: t('menu.primary.event.categorization'),
      },
      ...sortDashboards(computedUserDashboards.value)
        .map((d) => ({
          dashboardId: `${d.id}`,
          dashboardName: d.title,
        })),
    ];

    const sortedPages = pages.sort((a, b) => a.dashboardName.toLowerCase()
      .localeCompare(b.dashboardName.toLowerCase()));

    // Freeze the array to avoid converting every option into a reactive proxy
    // which can slow down rendering of large lists in components like v-select.
    return Object.freeze(sortedPages);
  });

  /**
   * Friendly name of the type of access of the current user
   */
  const accessFriendlyName = computed(() => {
    switch (activeUser.value?.access_code) {
      case 'administrator':
        return t('user.access_administrator');
      case 'manager':
        return t('user.access_manager');
      case 'operator':
        return t('user.access_operator');
      case 'simplified_operator':
        return t('user.access_simplified_operator');
      default:
        return '';
    }
  });

  /**
   * Default avatar
   */
  const defaultAvatar = computed(() => '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');

  /**
   * Get the user avatar if set otherwise get the default avatar
   */
  const avatar = computed(() => (activeUser.value?.imageUrl ? activeUser.value.imageUrl : `data:image/png;base64,${defaultAvatar.value}`));

  /**
   * Get the version of the application
   */
  const applicationVersion = computed(() => window.keyprod.version);

  /**
   * Get the user language
   */
  const userLanguage = computed(() => activeUser.value?.language || 'en');

  /**
   * Get the user homepage
   */
  const userHomepage = computed(() => activeUser.value?.starting_dashboard);

  /**
   * Get the user assigned groups
   */
  const userAssignedGroups = computed(() => activeUser.value?.groupsExpand);

  /**
   * Fetch the current user
   */
  async function fetchCurrentUser() {
    await store.dispatch('auth/fetchUser');
  }

  return {
    accessFriendlyName,
    activeUser,
    activeUserIsAdministratorOrManager,
    activeUserIsOperator,
    activeUserIsSimplifiedOperator,
    applicationVersion,
    avatar,
    avatarUrl,
    defaultAvatar,
    fetchCurrentUser,
    forgetDirectorySession,
    logout,
    logoutFromDirectory,
    resumeDirectoryLogout,
    signedInThroughDirectory,
    redirectToLogin,
    allowedHomepages,
    userAccessCode,
    userIsMe,
    userFirstLogin,
    userLanguage,
    userHomepage,
    userName,
    userAssignedGroups,
  };
}
